import type { DailyStanding } from '../../shared/api';
import type { GameMode } from '../../shared/modes';
import type { RankChange } from '../../shared/ranked';
import { tierName } from '../content/ranks';
import {
  neutralPlayerOrder,
  revealStageAt,
  revealTallyFraction,
  type RevealStage,
} from '../../shared/results';

export type { RevealStage };

export type RevealPlayback =
  | { kind: 'shared'; revealAt: number; serverNow: number; tallyMs: number }
  | { kind: 'local'; startedAt: number; now: number; tallyMs: number }
  | { kind: 'settled' };

function revealElapsed(playback: Exclude<RevealPlayback, { kind: 'settled' }>): number {
  return playback.kind === 'shared'
    ? playback.serverNow - playback.revealAt
    : playback.now - playback.startedAt;
}

/** Motion preferences change rendering, never the shared reveal timeline. */
export function stageForPlayback(playback: RevealPlayback): RevealStage {
  return playback.kind === 'settled' ? 'standings' : revealStageAt(revealElapsed(playback), playback.tallyMs);
}

/** Without motion, reveal static scores at the final stage rather than counting them up. */
export function tallyForPlayback(playback: RevealPlayback, reducedMotion: boolean): number {
  if (playback.kind === 'settled' || stageForPlayback(playback) === 'standings') return 1;
  return reducedMotion ? 0 : revealTallyFraction(revealElapsed(playback), playback.tallyMs);
}

/**
 * Players rank by running score, which reaches the final score at standings; ties keep the neutral
 * order so equal scores do not hint at the result. One order for the whole reveal means a row never
 * jumps when the recap ends.
 */
export function revealOrder<T extends { id: string }>(
  players: readonly T[],
  runningScore: (player: T) => number,
): T[] {
  return neutralPlayerOrder(players).sort((a, b) => runningScore(b) - runningScore(a));
}

/** Medals for the top three places in head-to-head standings. */
export type PodiumPlace = 'gold' | 'silver' | 'bronze';

const PODIUM: readonly PodiumPlace[] = ['gold', 'silver', 'bronze'];

/**
 * Competition ranks for players already in reveal order: tied scores share a rank and the next
 * rank skips past them (1, 1, 3).
 */
export function standingRanks<T>(ordered: readonly T[], score: (player: T) => number): number[] {
  return ordered.map((player) => 1 + ordered.filter((other) => score(other) > score(player)).length);
}

/** A podium medal for ranks 1–3 with points; nobody medals on zero. */
export function podiumPlace(rank: number, score: number): PodiumPlace | null {
  return score > 0 ? (PODIUM[rank - 1] ?? null) : null;
}

/** What a standings row shows in place of a rank once the recap finishes. */
export type Placing =
  | { readonly kind: 'rank'; readonly rank: number; readonly podium: PodiumPlace | null }
  | { readonly kind: 'tier'; readonly label: string }
  | { readonly kind: 'daily'; readonly rank: number };

export type PlacingKind = Placing['kind'] | null;

/**
 * How a mode places players: Versus ranks the room, Competitive shows tiers, Daily shows the
 * viewer's place on the day's leaderboard, and other solo modes show none.
 */
export function placingKind(mode: GameMode): PlacingKind {
  if (mode === 'versus') return 'rank';
  if (mode === 'competitive') return 'tier';
  if (mode === 'daily') return 'daily';
  return null;
}

/**
 * A row's placing, hidden until standings so the recap never shows a rank. Competitive shows the
 * player's tier after the match; a player without a visible rank change (a bot) shows none. Daily
 * shows the viewer's leaderboard place once it is known.
 */
export function placingFor(
  kind: PlacingKind,
  stage: RevealStage,
  rank: number,
  player: { readonly score: number; readonly rankChange?: RankChange | undefined },
  dailyStanding: DailyStanding | null,
): Placing | null {
  if (stage !== 'standings' || kind === null) return null;
  if (kind === 'rank') return { kind, rank, podium: podiumPlace(rank, player.score) };
  if (kind === 'daily') return dailyStanding ? { kind, ...dailyStanding } : null;
  return player.rankChange ? { kind, label: tierName(player.rankChange.after) } : null;
}
